// Authoritative simulation. step(dt, inputs) -> SimState.
// No DOM, no three.js — netcode boundary lives here.

import RAPIER from '@dimforge/rapier3d-compat';
import { CONFIG } from '../config';
import type { InputFrame } from '../core/InputFrame';
import { getBundle } from '../maps/registry';
import { MapArena } from './MapArena';
import { Character } from './Character';
import { Combat } from './Combat';
import { Grab } from './Grab';
import { Ragdoll, type CharGroups } from './Ragdoll';
import type { FootstepEvent, ImpactEvent, SimState, SplashEvent, SwingStartEvent, TeamId } from './SimState';

// Collision bit layout: bit 0 = arena; each character gets three consecutive
// bits (torso / limb / leg) — see CharGroups in Ragdoll.ts for the
// self-collision rules. 4 chars × 3 bits + 1 arena = 13 of 16 bits used.
const ARENA_GROUP = 1 << 0;
function charBits(i: number): { torso: number; limb: number; leg: number } {
  const base = 1 + i * 3;
  return { torso: 1 << base, limb: 1 << (base + 1), leg: 1 << (base + 2) };
}

export interface WorldOptions {
  playerIds: number[]; // simulation player ids; first is human, others AI
  mapId?: string;      // chosen map id (bundle.id); defaults to the sandbox
  /** Team assignment for each sim id. When omitted, every character is
   *  considered to be on its own single-character team — the round-end check
   *  then degenerates to the old "last one standing" behaviour. */
  teamById?: Record<number, TeamId>;
  /** True for single-player / server / editor playtest worlds; false for
   *  the multiplayer client's local World. Propagated into the MapArena
   *  so behaviors can branch their stepSim logic (run a state machine
   *  vs. apply replicated state, apply damage vs. trust the server). */
  authoritative?: boolean;
  /** Optional per-behavior param overrides. Keyed by behavior `id` from
   *  map.json (the `behaviors[].id` field). Merged on top of each
   *  behavior's map.json `params` at init time. Used by the multiplayer
   *  stack to thread match-scoped config (e.g. the PRNG seed for the
   *  asteroid field) without mutating the committed bundle. */
  behaviorParamOverrides?: Record<string, Record<string, unknown>>;
}

export class World {
  readonly rapier: RAPIER.World;
  /** Data-driven map driver built from a registered MapBundle. */
  readonly arena: MapArena;
  readonly characters: Character[] = [];
  readonly combat = new Combat();
  private eventQueue: RAPIER.EventQueue;
  private impactsBuffer: ImpactEvent[] = [];
  private tick = 0;
  private spawns: { x: number; z: number; y: number | undefined; yaw: number }[] = [];
  private opponentSets = new Map<number, Set<RAPIER.RigidBody>>();
  // Reverse map: any character bone's rigid body → the Character that owns it.
  // Grab uses this to resolve the body it just latched onto back to the
  // victim Character (for wriggle-bar writes + postWriggleLock checks).
  private bodyToCharacter = new Map<RAPIER.RigidBody, Character>();
  private roundOver = false;
  private winningTeam: TeamId | null = null;
  private graceTicks = 30; // ~0.5s — ignore ring-out checks while bones settle
  private teamById: Record<number, TeamId>;
  // Cached `Set(Object.values(teamById))`. teamById is fixed at construction
  // (we reassign it once in `constructor`), so the round-end loop doesn't
  // need to rebuild the set every tick.
  private teamsPresent!: Set<TeamId>;

  constructor(opts: WorldOptions) {
    const mapId = opts.mapId ?? 'sandbox';
    // Default every character to its own solo team ("t<id>") so the old
    // single-vs-single behaviour still works in tooling like benchmark.ts
    // that doesn't bother assigning teams.
    this.teamById = opts.teamById ?? Object.fromEntries(
      opts.playerIds.map((id) => [id, `t${id}` as unknown as TeamId])
    );
    this.teamsPresent = new Set(Object.values(this.teamById));
    const bundle = getBundle(mapId);
    if (!bundle) {
      throw new Error(
        `[World] map bundle not found for mapId "${mapId}". ` +
        `Expected a directory under src/maps/bundles/ whose map.json declares this id.`
      );
    }

    const gravityY = bundle.json.properties?.gravity ?? CONFIG.sim.gravity;
    this.rapier = new RAPIER.World({ x: 0, y: gravityY, z: 0 });
    this.eventQueue = new RAPIER.EventQueue(true);

    this.arena = new MapArena(this.rapier, bundle, {
      authoritative: opts.authoritative ?? true,
      behaviorParamOverrides: opts.behaviorParamOverrides
    });
    // Hand the arena a stable reference to our impacts buffer. World.step
    // clears it (length=0) and re-pushes each tick, so the captured pointer
    // always sees the latest events. Behaviors read via ctx.recentImpacts().
    this.arena.setImpactsRef(this.impactsBuffer);
    void ARENA_GROUP;

    // Spawn characters. Maps may declare explicit `spawns` in their map.json
    // (e.g. sketchy-airship puts players on the red/blue pads at either end);
    // maps without it fall back to the default 2.0 m circle at origin. Maps
    // that declare fewer spawns than there are players fall through to the
    // circle for the overflow indices so a mis-authored file can't crash.
    const n = opts.playerIds.length;
    const spawnRadius = 2.0;
    const authoredSpawns = bundle.json.spawns ?? [];
    const perChar = Array.from({ length: n }, (_, i) => charBits(i));
    for (let i = 0; i < n; i++) {
      let x: number, z: number, yaw: number;
      let y: number | undefined;
      const authored = authoredSpawns[i];
      if (authored) {
        x = authored.p[0];
        z = authored.p[1];
        yaw = authored.yaw ?? Math.atan2(-x, -z);
        y = authored.y;
      } else {
        const a = (i / n) * Math.PI * 2;
        x = Math.cos(a) * spawnRadius;
        z = Math.sin(a) * spawnRadius;
        yaw = Math.atan2(-x, -z);
      }
      this.spawns.push({ x, z, y, yaw });

      const id = opts.playerIds[i]!;
      const own = perChar[i]!;
      let opponent = 0;
      for (let j = 0; j < n; j++) {
        if (j === i) continue;
        const other = perChar[j]!;
        opponent |= other.torso | other.limb | other.leg;
      }
      const groups: CharGroups = {
        torso: own.torso, limb: own.limb, leg: own.leg,
        opponent, arena: ARENA_GROUP
      };
      const ragdoll = new Ragdoll(this.rapier, x, z, yaw, groups, y);
      const grab = new Grab(
        this.rapier,
        ragdoll,
        own.torso | own.limb | own.leg,
        () => this.opponentSetFor(id),
        (b) => this.bodyToCharacter.get(b) ?? null
      );
      const ch = new Character(
        id,
        ragdoll,
        grab,
        yaw,
        this.makeSurfaceYProvider(),
        this.arena.mapId !== 'tutorial'
      );
      this.characters.push(ch);
      this.combat.registerCharacter(ch);
    }

    // Build per-character opponent body sets + the global body→character map.
    for (const ch of this.characters) {
      const set = new Set<RAPIER.RigidBody>();
      for (const other of this.characters) {
        if (other === ch) continue;
        for (const b of other.ragdoll.handles.bodies) set.add(b);
      }
      this.opponentSets.set(ch.id, set);
      for (const b of ch.ragdoll.handles.bodies) this.bodyToCharacter.set(b, ch);
    }

    // Arena needs the character list for any map-specific damage checks
    // (currently: rock-impact KO in the spaceship map).
    this.arena.registerVictims(this.characters);
  }

  private opponentSetFor(id: number): Set<RAPIER.RigidBody> {
    return this.opponentSets.get(id) ?? new Set();
  }

  /** Build the per-character water-surface lookup. Composes the map's flat
   *  `properties.waterY` (the base plane) with any wave-providing behavior
   *  on the arena (currently only the sinking-ship ocean) so submersion +
   *  buoyancy track the live wavy surface. Returns null on dry maps. */
  private makeSurfaceYProvider(): ((x: number, z: number) => number) | null {
    const baseY = this.arena.json.properties?.waterY;
    if (baseY === undefined) return null;
    const provider = this.findWaveProvider();
    if (!provider) return () => baseY;
    return (x: number, z: number) => baseY + provider.sampleWaveOffset(x, z);
  }

  /** Walk the arena's behavior list looking for one that exposes the
   *  WaterWaveProvider duck-type (a `sampleWaveOffset(x, z)` method). Done
   *  structurally so we don't have to import the concrete behavior class
   *  here — any future wavy-water behavior just implements the same shape. */
  private findWaveProvider(): { sampleWaveOffset(x: number, z: number): number } | null {
    for (const b of this.arena.behaviorList) {
      const candidate = b as unknown as { sampleWaveOffset?: (x: number, z: number) => number };
      if (typeof candidate.sampleWaveOffset === 'function') {
        return candidate as { sampleWaveOffset(x: number, z: number): number };
      }
    }
    return null;
  }

  /** Apply one fixed-step. */
  step(dt: number, inputs: Map<number, InputFrame>): SimState {
    // 1) Drive each character with its input. Clear per-character event
    // buffers first so this tick's plant / swing events are the only ones
    // that ship in the outgoing snapshot.
    for (const ch of this.characters) {
      ch.footsteps.length = 0;
      ch.swings.length = 0;
      ch.splashes.length = 0;
    }
    for (const ch of this.characters) {
      const inp = inputs.get(ch.id);
      if (inp) ch.step(inp, dt);
    }

    // 1b) Honor wriggle-escape requests: any character whose wriggle bar
    // hit 0 this tick gets every attacker's Grab joints on it released.
    for (const victim of this.characters) {
      if (!victim.wriggleEscapeRequested) continue;
      victim.wriggleEscapeRequested = false;
      for (const attacker of this.characters) {
        if (attacker === victim) continue;
        attacker.grab.releaseAllOn(victim);
      }
    }

    // 2) Step Rapier
    this.rapier.timestep = dt;
    this.rapier.step(this.eventQueue);

    // 2a) Drive every map behavior: rope PD springs, spaceship tilt state
    // machine + kinematic rotation, asteroid drift / recycle / KO checks,
    // etc. Runs AFTER the solver so contact impulses from characters leaning
    // on dynamic map geometry get displaced first. `roundOver` is passed so
    // behaviors can suppress gameplay-side effects (KO damage from a late
    // meteor during the celebration modal) while still stepping visuals.
    // Authoritative sim-time for this tick. Identical on server and every
    // client because all peers' Worlds advance `tick` in lockstep — letting
    // behaviors that need cross-peer time sync (e.g. wave phase) consume
    // the same value everywhere instead of reaching for `performance.now()`.
    const simTimeSec = this.tick / CONFIG.sim.tickHz;
    this.arena.stepSim(dt, this.roundOver, simTimeSec);

    // 2a3) Wind shove: push every character in the wind direction. Applied
    // to every bone (not just the pelvis) so a grounded character with
    // planted feet still gets dragged — otherwise the leg joints anchor
    // against the floor friction and the body just leans slightly instead
    // of sliding. F = m·a per bone keeps the acceleration constant across
    // the ragdoll so the whole body accelerates uniformly. No-op on maps
    // without wind (returns {0,0}).
    const w = this.arena.windAccel();
    if (w.x !== 0 || w.z !== 0) {
      for (const ch of this.characters) {
        const bones = ch.ragdoll.handles.bodies;
        for (const b of bones) {
          if (!b) continue;
          const m = b.mass();
          b.applyImpulse(
            { x: w.x * m * dt, y: 0, z: w.z * m * dt },
            true
          );
        }
      }
    }

    // 2b) Hard safety net: clamp every bone's vel/angvel so an out-of-range
    // impulse can't fragment the ragdoll across the stage.
    for (const ch of this.characters) {
      ch.ragdoll.clampVelocities(CONFIG.character.maxBoneLinVel, CONFIG.character.maxBoneAngVel);
    }

    // 3) Process collisions → impacts. Suppressed once the round is over so
    // a stray punch / body-on-body slam during the end-of-round cinematic
    // can't retrigger a KO or bump koStunRemaining back up while inputs are
    // frozen — that stale timer would otherwise survive into the next round
    // and present as a few seconds of "can't move" at spawn.
    this.impactsBuffer.length = 0;
    if (!this.roundOver) {
      this.combat.process(this.rapier, this.eventQueue, this.impactsBuffer);
    } else {
      // Still drain so events don't back up for the next round.
      this.eventQueue.drainCollisionEvents(() => {});
    }

    // 4) Ring-out check (skipped during grace period right after spawn / reset)
    if (this.graceTicks > 0) {
      this.graceTicks -= 1;
    } else {
      // Update ring-out flags first.
      for (const ch of this.characters) {
        if (!ch.ringedOut) {
          const t = ch.ragdoll.pelvis().translation();
          if (Number.isFinite(t.x) && Number.isFinite(t.y) && Number.isFinite(t.z)
              && this.arena.isOutOfBounds(t.x, t.y, t.z)) {
            ch.ringedOut = true;
            ch.knockedOut = true;
          }
        }
      }
      // Team-aware round end: a round ends the instant one team has zero
      // alive characters (wasted OR ringedOut). HP-zero and ring-out are
      // treated identically. Surviving team wins; simultaneous wipes →
      // winningTeam = null (draw — match layer re-plays the round without
      // awarding a crown).
      if (!this.roundOver) {
        const aliveByTeam = new Map<TeamId, number>();
        for (const ch of this.characters) {
          if (ch.ringedOut || ch.wasted) continue;
          const team = this.teamById[ch.id];
          if (!team) continue;
          aliveByTeam.set(team, (aliveByTeam.get(team) ?? 0) + 1);
        }
        const aliveTeams: TeamId[] = [];
        for (const t of this.teamsPresent) {
          if ((aliveByTeam.get(t) ?? 0) > 0) aliveTeams.push(t);
        }
        // End the round when ≤1 team still has living members. For solo
        // (fallback) mode with N characters on N solo teams, this collapses
        // back to "one character left".
        if (this.teamsPresent.size >= 2 && aliveTeams.length <= 1) {
          this.roundOver = true;
          this.winningTeam = aliveTeams[0] ?? null;
          // Round just ended this tick — retroactively clear any KO / stun
          // timers that were still counting down. Input is frozen from here
          // until the match layer calls forceReset(), and the freeze skips
          // Character.step() entirely — without this sweep a koStunRemaining
          // that was set in the same fatal tick would stay latched at its
          // full value (e.g. 5s) and then present as residual stun after
          // the next round spawns. Ragdoll velocities are untouched so
          // bodies keep flopping visibly for the celebration.
          for (const ch of this.characters) {
            ch.koStunRemaining = 0;
            ch.stunRemaining = 0;
            ch.knockedOut = false;
          }
        }
      }
    }
    // Round-end auto-reset was removed — the match layer (LocalTransport)
    // owns the pacing now. It triggers the modal and calls `forceReset()`
    // once the user / modal timer asks for the next round.

    this.tick += 1;

    // 5) Build snapshot
    const snapshots = this.characters.map((ch, i) => {
      const snap = Character.makeSnapshot(ch.id);
      ch.writeSnapshot(snap);
      return snap;
    });

    // Single-pass event aggregation across all characters. The previous code
    // walked `this.characters` three times (once per event kind); this merge
    // keeps the same output ordering but only reads each character once.
    const footstepsBuffer: FootstepEvent[] = [];
    const swingsBuffer: SwingStartEvent[] = [];
    const splashesBuffer: SplashEvent[] = [];
    for (let ci = 0; ci < this.characters.length; ci++) {
      const ch = this.characters[ci]!;
      const fs = ch.footsteps;
      for (let i = 0; i < fs.length; i++) footstepsBuffer.push(fs[i]!);
      const sw = ch.swings;
      for (let i = 0; i < sw.length; i++) swingsBuffer.push(sw[i]!);
      const sp = ch.splashes;
      for (let i = 0; i < sp.length; i++) splashesBuffer.push(sp[i]!);
    }

    return {
      tick: this.tick,
      characters: snapshots,
      impacts: this.impactsBuffer.slice(),
      footsteps: footstepsBuffer,
      swings: swingsBuffer,
      splashes: splashesBuffer,
      roundOver: this.roundOver,
      winningTeam: this.winningTeam,
      teamById: this.teamById
    };
  }

  resetRound(): void {
    this.roundOver = false;
    this.winningTeam = null;
    this.graceTicks = 30;
    for (let i = 0; i < this.characters.length; i++) {
      const s = this.spawns[i]!;
      this.characters[i]!.reset(s.x, s.z, s.yaw, s.y);
    }
    // Map behaviors with per-round timers (sinking-ship sink countdown,
    // etc.) re-zero alongside the character respawn so countdown clocks
    // restart in lockstep with the round.
    this.arena.resetRound();
  }

  forceReset(): void { this.resetRound(); }
}
